// @ts-check
import { escapeHtml } from './html.js';

/**
 * Status and rating chrome for an image, shared by the grid cards and the table.
 *
 * The grid used to stack three absolutely positioned badges — Active, User,
 * Unclassified — down the corner of every thumbnail, covering the artwork they
 * described and repeating what the buttons underneath already said. What is left
 * here is the rule for the two facts a card cannot show any other way.
 */

/**
 * Human label for an NSFW verdict. `null` means the classifier has not answered
 * yet, which is a queue state rather than a rating.
 *
 * @param {number|null|undefined} isNsfw
 * @returns {'NSFW'|'SFW'|'Unrated'}
 */
export function ratingLabel(isNsfw) {
  if (isNsfw === 1) return 'NSFW';
  if (isNsfw === 0) return 'SFW';
  return 'Unrated';
}

/**
 * Thumbnail overlay for a rating, and only when the image wants an operator's
 * attention. A confirmed-safe image gets nothing: the common case should be quiet.
 *
 * @param {number|null|undefined} isNsfw
 * @returns {string}
 */
export function ratingBadgeHtml(isNsfw) {
  const label = ratingLabel(isNsfw);
  if (label === 'SFW') return '';
  if (label === 'NSFW') {
    return '<span class="card-rating-badge rating-nsfw">NSFW</span>';
  }
  return '<span class="card-rating-badge rating-unrated" title="No NSFW verdict recorded yet">Unrated</span>';
}

/**
 * The dot that rides inside the Enable / Disable button, so publish state sits on
 * the control that changes it rather than in a badge of its own.
 *
 * @param {boolean|number|null|undefined} isActive
 * @returns {string}
 */
export function statusDotHtml(isActive) {
  const cls = isActive ? 'status-dot-active' : 'status-dot-inactive';
  return '<span class="status-dot ' + cls + '" aria-hidden="true"></span>';
}

/**
 * Tooltip for the toggle. The dot alone carries no meaning for a screen reader or
 * a first-time operator, so the state is spelled out with the action it offers.
 *
 * @param {boolean|number|null|undefined} isActive
 * @returns {string}
 */
export function toggleButtonTitle(isActive) {
  return isActive ? 'Active — click to disable' : 'Inactive — click to enable';
}

/**
 * Tooltip for the Feature control.
 *
 * @param {boolean|number|null|undefined} isFeatured
 * @returns {string}
 */
export function featureButtonTitle(isFeatured) {
  return isFeatured ? 'Starred — click to unstar' : 'Not starred — click to star';
}

/**
 * Markup for the Enable / Disable control used on grid cards and the table.
 *
 * @param {{ id: string, is_active?: boolean|number|null }} img
 * @param {string} [extraClass]
 * @returns {string}
 */
export function publishToggleButtonHtml(img, extraClass = '') {
  const active = !!img.is_active;
  const cls = ('btn btn-secondary btn-sm toggle-btn ' + extraClass).trim();
  return (
    '<button type="button" class="' + cls + '" data-id="' + img.id + '" data-active="' + (active ? 1 : 0) + '" title="' + toggleButtonTitle(active) + '">' +
      statusDotHtml(active) +
      (active ? 'Disable' : 'Enable') +
    '</button>'
  );
}

/**
 * Markup for Star / Unstar.
 *
 * Grid cards use `variant: 'star'` (icon on the thumbnail). The images table
 * puts the labelled button in its own column.
 *
 * @param {{ id: string, is_featured?: boolean|number|null }} img
 * @param {string} [extraClass]
 * @param {'button'|'star'} [variant]
 * @returns {string}
 */
export function featureToggleButtonHtml(img, extraClass = '', variant = 'button') {
  const featured = !!img.is_featured;
  const title = featureButtonTitle(featured);
  if (variant === 'star') {
    const cls = ('feature-btn feature-star-btn ' + extraClass).trim();
    return (
      '<button type="button" class="' + cls + '" data-id="' + img.id + '" data-featured="' + (featured ? 1 : 0) + '" title="' + title + '" aria-label="' + title + '" aria-pressed="' + (featured ? 'true' : 'false') + '">' +
        (featured ? '★' : '☆') +
      '</button>'
    );
  }
  const cls = ('btn btn-secondary btn-sm feature-btn ' + extraClass).trim();
  return (
    '<button type="button" class="' + cls + '" data-id="' + img.id + '" data-featured="' + (featured ? 1 : 0) + '" title="' + title + '">' +
      (featured ? 'Unstar' : 'Star') +
    '</button>'
  );
}


/**
 * Renders the Source column content for an image row.
 * For user uploads, logs/displays the uploading user's ID.
 *
 * @param {{ is_user_contributed?: boolean|number|null, user_id?: string|null }} img
 * @param {(value: unknown) => string} [monoFormatter]
 * @returns {string}
 */
export function imageSourceHtml(img, monoFormatter) {
  if (!img.is_user_contributed) {
    return '<span title="Uploaded by admin">Admin</span>';
  }
  const uId = img.user_id ? String(img.user_id).trim() : '';
  if (!uId) {
    return '<span title="Contributed by a player">User</span>';
  }
  const mono = monoFormatter
    ? monoFormatter(uId)
    : '<span class="mono">' + escapeHtml(uId) + '</span>';
  return '<span title="Contributed by player ' + escapeHtml(uId) + '">User</span><br>' + mono;
}
